import * as React from "react";
import { Switch } from "../../bits/Switch/Switch";
import styles from "./HistoryWrapper.module.scss";
import ReserveTime from "./ReserveTime/ReserveTime";
import TimeOff from "./TimeOff/TimeOff";
import {
    AvailabilityStatus,
    IAvailabilitySchedule,
    IClinician,
    IReservedSchedule,
} from "intake_shared/src/types";
import store from "../../../../store";
import { AclContext } from "../../../../contexts/AclContext";

interface HistoryWrapperProps {
    loggedInUserEmailProp?: string;
}

export default function HistoryWrapper({
    loggedInUserEmailProp,
}: HistoryWrapperProps) {
    const aclContext = React.useContext(AclContext);
    const loggedInUserEmail =
        loggedInUserEmailProp || aclContext?.authUser.user?.email;

    const [activeSwitch, setActiveSwitch] = React.useState("reserve");
    const [loggedInClinician, setLoggedInClinician] = React.useState(
        {} as IClinician
    );

    const [availabilitySchedules, setAvailabilitySchedules] = React.useState(
        [] as IAvailabilitySchedule[]
    );
    const [unavailabilitySchedules, setUnavailabilitySchedules] = React.useState(
        [] as IAvailabilitySchedule[]
    );

    const handleSwitch = (key: string) => {
        setActiveSwitch(key);
    };

    const getClinician = async (loggedInUserEmail: string) => {
        const clinician = await store.getClinicianByEmail(loggedInUserEmail);
        if (clinician) {
            clinician.held = (clinician.held || []).filter((h) => h.id);
            clinician.confirmed = (clinician.confirmed || []).filter((c) => c.id);
            setLoggedInClinician(clinician);
            setAvailabilitySchedules(clinician.available);
            setUnavailabilitySchedules(clinician.unavailable);
        }
    };

    React.useEffect(() => {
        if (loggedInUserEmail) {
            getClinician(loggedInUserEmail);
        }
    }, [loggedInUserEmail]);

    const manageAvailability = async (
        schedule: IAvailabilitySchedule | IReservedSchedule
    ) => {
        let scheduleIdx = availabilitySchedules.findIndex(
            (s) => s.id === schedule.id
        );
        if (scheduleIdx > -1) {
            const updatedAvailabilitySchedules = [...availabilitySchedules];
            updatedAvailabilitySchedules.splice(scheduleIdx, 1);
            await store.updateClinicianAvailability(
                loggedInClinician.id || loggedInClinician.email,
                AvailabilityStatus.AVAILABLE,
                updatedAvailabilitySchedules
            );
            if ((schedule as IReservedSchedule).reservedFor) {
                await store.deleteReservedAppointment(schedule);
            }
            setAvailabilitySchedules([...updatedAvailabilitySchedules]);
        } else {
            scheduleIdx = unavailabilitySchedules.findIndex(
                (s) => s.id === schedule.id
            );
            const updatedAvailabilitySchedules = [...unavailabilitySchedules];
            updatedAvailabilitySchedules.splice(scheduleIdx, 1);
            await store.updateClinicianAvailability(
                loggedInClinician.id || loggedInClinician.email,
                AvailabilityStatus.UNAVAILABLE,
                updatedAvailabilitySchedules
            );
            setUnavailabilitySchedules([...updatedAvailabilitySchedules]);
        }
    };

    return (
        <div className={styles.historyWrapper}>
            <div className={styles.historyHeader}>
                <Switch
                    switchOptions={[
                        {
                            title: "Reserve time",
                            key: "reserve",
                            onClick: handleSwitch,
                        },
                        {
                            title: "Time off",
                            key: "timeoff",
                            onClick: handleSwitch,
                        },
                    ]}
                    activeSwitch={activeSwitch}
                />
            </div>
            <div className={styles.historyContent}>
                {activeSwitch === "reserve" && (
                    <ReserveTime
                        reservedWindows={(
                            availabilitySchedules as IReservedSchedule[]
                        ).filter((a) => a.reservedFor)}
                        onRemoveSchedule={(schedule: IAvailabilitySchedule) => {
                            manageAvailability(schedule);
                        }}
                    />
                )}
                {activeSwitch === "timeoff" && (
                    <TimeOff
                        timeOff={unavailabilitySchedules as IReservedSchedule[]}
                        onRemoveSchedule={(schedule: IAvailabilitySchedule) => {
                            manageAvailability(schedule);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
